import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { formatXOF } from "../../lib/i18n";
import { toast } from "sonner";
import {
  CalendarDays,
  List as ListIcon,
  Search,
  X,
  Mail,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Download,
  AlertTriangle,
  FileSpreadsheet,
  FileText as FileTextIcon,
  TicketCheck,
  CheckCircle2,
} from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════
// UNIVERSE MAPPING — mirrors UniverseDashboard.jsx (single source of truth)
// ═══════════════════════════════════════════════════════════════════════
const UNIVERSES = {
  hotel: {
    label: "Hôtel",
    offer_types: [
      "hebergement", "salles", "seminaire", "team_building",
      "journee_etude", "dejeuner_diner_entreprise", "formule_personnalisee",
      "le_kaai", "lounge", "spa_wellness",
    ],
  },
  beach_life: {
    label: "Beach Life",
    offer_types: [
      "pass_day", "sunset", "brunch", "offres_loisirs", "special_event",
    ],
  },
};

const OFFER_DOT = {
  pass_day: "bg-[#B8922A]",
  sunset: "bg-orange-500",
  brunch: "bg-green-600",
  le_kaai: "bg-purple-500",
  hebergement: "bg-blue-600",
  spa_wellness: "bg-pink-500",
  seminaire: "bg-slate-600",
  team_building: "bg-slate-500",
  offres_loisirs: "bg-cyan-500",
  special_event: "bg-rose-600",
};

const PAYMENT_METHOD_FR = {
  cash: "Espèces",
  card: "Carte bancaire",
  mobile_money: "Mobile Money",
  fineo: "FINEO",
  deposit: "Acompte",
};

// ═══════════════════════════════════════════════════════════════════════
// PAYMENT BADGE — only two states: "Acompte 30%" OR "Soldé"
// (matches the user's explicit request; unpaid rows never appear in this
// list because pending is excluded server-side)
// ═══════════════════════════════════════════════════════════════════════
function PaymentBadge({ booking }) {
  const balance = Number(booking.balance_due || 0);
  const paid = !!booking.paid_at;
  const isDeposit = booking.payment_method === "deposit" || (paid && balance > 0);

  if (isDeposit) {
    const pct = booking.deposit_pct || 30;
    return (
      <span
        className="text-[0.6rem] uppercase tracking-[0.18em] px-2 py-0.5 border bg-[#FFF6DA] text-[#8b6f16] border-[#B8922A]/40 whitespace-nowrap"
        data-testid="payment-acompte"
      >
        Acompte {pct}%
      </span>
    );
  }
  if (paid) {
    return (
      <span
        className="text-[0.6rem] uppercase tracking-[0.18em] px-2 py-0.5 border bg-green-50 text-green-700 border-green-200 whitespace-nowrap"
        data-testid="payment-solde"
      >
        Soldé
      </span>
    );
  }
  // Fallback (should not occur since pending excluded) — surface as impayé
  return (
    <span className="text-[0.6rem] uppercase tracking-[0.18em] px-2 py-0.5 border bg-red-50 text-red-700 border-red-200 whitespace-nowrap">
      Impayé
    </span>
  );
}

// iter-108 · Renders a single offer entry as an <option>, expanding
// Hébergement room tiers into individual filter targets so staff can drill
// down to Chambres Exclusives / Suite Jardin / Suite Lagune.
function FilterOfferOptions({ o }) {
  if (o.kind === "special_event") {
    const tag = o.is_agent_billed ? " · Par genre" : "";
    const state = o.is_active ? "" : " (inactif)";
    return (
      <option value={`event:${o.special_event_id}`}>
        {o.name_fr}{tag}{state}
      </option>
    );
  }
  const suffix = o.is_hidden ? " (inactif)" : "";
  const tiers = o.room_tiers || [];
  if (tiers.length > 0) {
    return (
      <>
        <option value={`offer:${o.offer_type}`}>{o.name_fr} — Toutes chambres{suffix}</option>
        {tiers.map((t) => (
          <option key={t.id} value={`offer:${o.offer_type}:${t.id}`}>
            {o.name_fr} — {t.name_fr}
          </option>
        ))}
      </>
    );
  }
  return <option value={`offer:${o.offer_type}`}>{o.name_fr}{suffix}</option>;
}

// ═══════════════════════════════════════════════════════════════════════
// LIST VIEW
// ═══════════════════════════════════════════════════════════════════════
function BookingsList({ bookings, onOpen }) {
  if (bookings.length === 0) {
    return (
      <p className="text-sm text-[#0A0A0A]/45 py-12 text-center" data-testid="bookings-empty">
        Aucune réservation pour ces filtres.
      </p>
    );
  }
  return (
    <div data-testid="bookings-list-wrap">
      <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/45 px-3 pb-2">
        ↓ Tri : du plus récent au plus ancien
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[1050px]" data-testid="bookings-table">
          <thead>
            <tr className="text-left text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 border-b border-[#0A0A0A]/10">
              <th className="py-3 px-3">Code</th>
              <th className="py-3 px-3">Date rés.</th>
              <th className="py-3 px-3">Date exp.</th>
              <th className="py-3 px-3">Boat time</th>
              <th className="py-3 px-3">Client</th>
              <th className="py-3 px-3">Téléphone</th>
              <th className="py-3 px-3">Offre</th>
              <th className="py-3 px-3">Pax</th>
              <th className="py-3 px-3 text-right">Total</th>
              <th className="py-3 px-3">Paiement</th>
              <th className="py-3 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {bookings.map((b) => {
              const primary = b.participants?.[0];
              const created = b.created_at ? new Date(b.created_at).toLocaleDateString("fr-FR") : "—";
              const pax = (b.adults || 0) + (b.children_paid || 0) + (b.children_free || 0) + (b.children || 0) - (b.children_paid || 0) - (b.children_free || 0);
              return (
                <tr
                  key={b.id}
                  onClick={() => onOpen(b.id)}
                  className="border-b border-[#0A0A0A]/5 hover:bg-[#FAFAF7] cursor-pointer transition-colors"
                  data-testid={`booking-${b.id.slice(0, 8)}`}
                >
                  <td className="py-3 px-3 tabular-nums text-[#B8922A] font-mono text-[0.85rem]" data-testid={`code-${b.id.slice(0,8)}`}>
                    {b.reservation_code || "—"}
                  </td>
                  <td className="py-3 px-3 tabular-nums text-[#0A0A0A]/70">{created}</td>
                  <td className="py-3 px-3 tabular-nums">{b.date || "—"}</td>
                  <td className="py-3 px-3 tabular-nums text-[#B8922A]">{b.boat_time || "—"}</td>
                  <td className="py-3 px-3">
                    <div className="text-[#0A0A0A] font-medium">
                      {primary ? `${primary.surname || ""} ${primary.name || ""}`.trim() : (b.surname || b.name || "—")}
                    </div>
                  </td>
                  <td className="py-3 px-3 text-[0.78rem] text-[#0A0A0A]/70">{b.phone || "—"}</td>
                  <td className="py-3 px-3">
                    <span className="inline-flex items-center gap-2">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${OFFER_DOT[b.offer_type] || "bg-[#0A0A0A]/30"}`} />
                      {b.offer_name || b.offer_type}
                    </span>
                  </td>
                  <td className="py-3 px-3 tabular-nums" title="Adultes + Enfants">
                    {pax || (b.adults + (b.children || 0))}
                  </td>
                  <td className="py-3 px-3 text-right tabular-nums">
                    {b.total_amount > 0 ? formatXOF(b.total_amount) : "—"}
                  </td>
                  <td className="py-3 px-3">
                    <PaymentBadge booking={b} />
                  </td>
                  <td className="py-3 px-3 text-right text-[0.7rem] text-[#B8922A]">Voir →</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CALENDAR VIEW (kept mostly as-is for continuity)
// ═══════════════════════════════════════════════════════════════════════
function BookingsCalendar({ month, onChangeMonth, byDate, onOpen }) {
  const [year, m] = month.split("-").map(Number);
  const firstDay = new Date(year, m - 1, 1);
  const startWeekday = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, m, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const monthLabel = firstDay.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const changeMonth = (delta) => {
    const nm = new Date(year, m - 1 + delta, 1);
    onChangeMonth(`${nm.getFullYear()}-${String(nm.getMonth() + 1).padStart(2, "0")}`);
  };
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="bookings-calendar">
      <div className="flex items-center justify-between mb-5">
        <button onClick={() => changeMonth(-1)} className="p-2 hover:bg-[#FAFAF7]" data-testid="cal-prev">
          <ChevronLeft size={16} />
        </button>
        <div className="font-display-serif text-2xl text-[#0A0A0A] capitalize">{monthLabel}</div>
        <button onClick={() => changeMonth(1)} className="p-2 hover:bg-[#FAFAF7]" data-testid="cal-next">
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((d) => (
          <div key={d} className="text-[0.6rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55 text-center pb-2">
            {d}
          </div>
        ))}
        {cells.map((d, idx) => {
          if (d === null) return <div key={`e-${idx}`} />;
          const iso = `${year}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const dayBookings = byDate[iso] || [];
          const isToday = iso === today;
          return (
            <div
              key={iso}
              className={`min-h-[110px] border p-2 ${isToday ? "border-[#B8922A] bg-[#B8922A]/5" : "border-[#0A0A0A]/8 bg-white"}`}
              data-testid={`cal-day-${iso}`}
            >
              <div className="text-[0.7rem] tabular-nums text-[#0A0A0A]/60 mb-1">{d}</div>
              <div className="space-y-1">
                {dayBookings.slice(0, 4).map((b) => (
                  <button
                    key={b.id}
                    onClick={() => onOpen(b.id)}
                    className="w-full text-left flex items-center gap-1.5 text-[0.65rem] leading-tight hover:opacity-80"
                  >
                    <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${OFFER_DOT[b.offer_type] || "bg-[#0A0A0A]/30"}`} />
                    <span className="truncate text-[#0A0A0A]/75">
                      {b.boat_time || ""} · {(b.adults || 0) + (b.children || 0)}p
                    </span>
                  </button>
                ))}
                {dayBookings.length > 4 && (
                  <div className="text-[0.6rem] text-[#B8922A]">+{dayBookings.length - 4}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// DETAIL DRAWER
// ═══════════════════════════════════════════════════════════════════════
// DETAIL DRAWER · iter-108
// Exhaustive booking view organised in editorial sections:
//   Réservation → Client → Participants (detailed by category)
//   → Détail financier (line-item breakdown) → Historique → Actions
// Agent-billed events (event_kind='agent_billed') use gender-based breakdowns
// (Hommes / Femmes) with the corresponding male/female prices instead of the
// classic Adultes / Enfants split.
// ═══════════════════════════════════════════════════════════════════════
const SOURCE_LABEL_FR = {
  public: "Site public",
  staff: "Réservation staff",
  agent: "Agent commercial",
  concierge: "Conciergerie",
  ota: "OTA / Distribution",
};

function fmtFrDateTime(iso) {
  if (!iso) return "Non renseigné";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
function fmtFrDate(iso) {
  if (!iso) return "Non renseigné";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch {
    return iso;
  }
}

function DrawerSection({ label, testid, children }) {
  return (
    <section className="pt-5 first:pt-0" data-testid={testid}>
      <div className="text-[0.6rem] uppercase tracking-[0.32em] text-[#B8922A] mb-3">{label}</div>
      {children}
    </section>
  );
}

function DrawerRow({ label, value, testid }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-[#0A0A0A]/6 last:border-b-0" data-testid={testid}>
      <span className="text-[0.72rem] uppercase tracking-[0.14em] text-[#0A0A0A]/55">{label}</span>
      <span className="text-sm text-[#0A0A0A] text-right font-medium">{value ?? "Non renseigné"}</span>
    </div>
  );
}

function ParticipantsBreakdown({ b }) {
  const isAgentBilled = !!b.is_agent_billed || Number(b.men_count || 0) + Number(b.women_count || 0) > 0;
  if (isAgentBilled) {
    const men = Number(b.men_count || 0);
    const women = Number(b.women_count || 0);
    return (
      <div className="bg-[#FAFAF7] p-4 space-y-1.5 text-sm" data-testid="participants-gender">
        <div className="flex justify-between"><span>Hommes</span><strong>{men}</strong></div>
        <div className="flex justify-between"><span>Femmes</span><strong>{women}</strong></div>
        <div className="flex justify-between border-t border-[#0A0A0A]/10 pt-1.5 mt-1">
          <span className="text-[#0A0A0A]/70">Total</span>
          <strong>{men + women} participant{men + women > 1 ? "s" : ""}</strong>
        </div>
      </div>
    );
  }
  const adults = Number(b.adults || 0);
  const paid = Number(b.children_paid ?? 0);
  const free = Number(b.children_free ?? 0);
  // Legacy fallback — some old bookings only carry `children` (no paid/free split)
  const legacyChildren = Number(b.children || 0);
  const kidsFallback = paid + free === 0 && legacyChildren > 0;
  const total = adults + (kidsFallback ? legacyChildren : paid + free);
  return (
    <div className="bg-[#FAFAF7] p-4 space-y-1.5 text-sm" data-testid="participants-classic">
      <div className="flex justify-between"><span>Adultes</span><strong>{adults}</strong></div>
      {kidsFallback ? (
        <div className="flex justify-between"><span>Enfants</span><strong>{legacyChildren}</strong></div>
      ) : (
        <>
          {(paid > 0 || free > 0) && (
            <>
              <div className="flex justify-between"><span>Enfants payants (6–12 ans)</span><strong>{paid}</strong></div>
              <div className="flex justify-between"><span>Enfants gratuits (&lt; 6 ans)</span><strong>{free}</strong></div>
            </>
          )}
        </>
      )}
      <div className="flex justify-between border-t border-[#0A0A0A]/10 pt-1.5 mt-1">
        <span className="text-[#0A0A0A]/70">Total</span>
        <strong>{total} personne{total > 1 ? "s" : ""}</strong>
      </div>
    </div>
  );
}

function FinancialBreakdown({ b }) {
  const isAgentBilled = !!b.is_agent_billed || Number(b.men_count || 0) + Number(b.women_count || 0) > 0;
  const rows = [];
  if (isAgentBilled) {
    const men = Number(b.men_count || 0);
    const women = Number(b.women_count || 0);
    const pM = Number(b.price_male_at_booking ?? b.price_male ?? 0);
    const pF = Number(b.price_female_at_booking ?? b.price_female ?? 0);
    if (men > 0) rows.push({ label: `${men} Homme${men > 1 ? "s" : ""}`, unit: pM, qty: men, total: men * pM });
    if (women > 0) rows.push({ label: `${women} Femme${women > 1 ? "s" : ""}`, unit: pF, qty: women, total: women * pF });
  } else {
    const adults = Number(b.adults || 0);
    const paid = Number(b.children_paid ?? 0);
    const free = Number(b.children_free ?? 0);
    const pA = Number(b.price_adult_at_booking ?? b.price_adult ?? 0);
    const pC = Number(b.price_child_at_booking ?? b.price_child ?? 0);
    if (adults > 0) rows.push({ label: `${adults} Adulte${adults > 1 ? "s" : ""}`, unit: pA, qty: adults, total: adults * pA });
    if (paid > 0) rows.push({ label: `${paid} Enfant${paid > 1 ? "s" : ""} payant${paid > 1 ? "s" : ""} (6–12 ans)`, unit: pC, qty: paid, total: paid * pC });
    if (free > 0) rows.push({ label: `${free} Enfant${free > 1 ? "s" : ""} gratuit${free > 1 ? "s" : ""} (< 6 ans)`, unit: 0, qty: free, total: 0 });
  }
  // Hébergement chambres — one visible line per tier
  if (b.room_tier_name) {
    const nights = Number(b.nights || 0);
    const rooms = Number(b.rooms || 1);
    const price = Number(b.room_tier_price || 0);
    const totalR = rooms * nights * price;
    rows.push({
      label: `${rooms} × ${b.room_tier_name}${nights > 0 ? ` · ${nights} nuit${nights > 1 ? "s" : ""}` : ""}`,
      unit: price,
      qty: rooms * (nights || 1),
      total: totalR,
    });
  }
  const grand = Number(b.total_amount || 0);
  return (
    <div className="bg-[#FAFAF7] p-4 text-sm" data-testid="financial-breakdown">
      {rows.length === 0 ? (
        <p className="text-[#0A0A0A]/55">Aucun détail tarifaire enregistré.</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r, i) => (
            <li key={i} className="flex justify-between gap-3">
              <span className="text-[#0A0A0A]/80">
                {r.label}{r.unit > 0 ? ` × ${formatXOF(r.unit)}` : ""}
              </span>
              <strong className="tabular-nums">{r.total > 0 ? formatXOF(r.total) : "0 FCFA"}</strong>
            </li>
          ))}
        </ul>
      )}
      <div className="border-t border-[#0A0A0A]/15 mt-3 pt-2 flex justify-between text-sm">
        <span className="text-[#0A0A0A]/70 uppercase tracking-[0.14em] text-[0.7rem]">Total</span>
        <strong className="tabular-nums text-[#B8922A]">{formatXOF(grand)}</strong>
      </div>
      <div className="mt-2 flex justify-between text-[0.82rem]">
        <span className="text-[#0A0A0A]/55">Payé</span>
        <span className="tabular-nums">{formatXOF(b.paid_amount || 0)}</span>
      </div>
      {Number(b.balance_due || 0) > 0 && (
        <div className="mt-1 flex justify-between text-[0.82rem]">
          <span className="text-[#0A0A0A]/55">Solde restant</span>
          <span className="tabular-nums text-[#B8922A] font-medium">{formatXOF(b.balance_due)}</span>
        </div>
      )}
    </div>
  );
}

function BookingDrawer({ id, onClose }) {
  const [b, setB] = useState(null);

  useEffect(() => {
    if (!id) return;
    api.get(`/staff/bookings/${id}`)
      .then((r) => setB(r.data))
      .catch(() => {
        toast.error("Impossible de charger cette réservation");
        onClose?.();
      });
  }, [id, onClose]);

  if (!id) return null;
  const primary = b?.participants?.[0] || {};
  const clientName = `${primary.surname || ""} ${primary.name || ""}`.trim()
    || `${b?.surname || ""} ${b?.name || ""}`.trim()
    || "Non renseigné";
  const sourceLabel = b ? (SOURCE_LABEL_FR[b.source] || b.source || "Site public") : "";
  const universeFR = b?.pole ? ({
    beach_club: "Beach Life", hebergement: "Hôtel", corporate: "Corporate",
    activites_events: "Activités & Événements", le_kaai: "Restaurant Le Kaai",
  }[b.pole] || b.pole) : "—";

  return (
    <div className="fixed inset-0 z-40" data-testid="booking-drawer">
      <div className="absolute inset-0 bg-[#0A0A0A]/30" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#0A0A0A]/10 px-6 py-4 flex items-center justify-between z-10">
          <div className="text-[0.6rem] uppercase tracking-[0.28em] text-[#B8922A]">Détail réservation</div>
          <button onClick={onClose} className="hover:bg-[#FAFAF7] p-1" data-testid="drawer-close">
            <X size={16} />
          </button>
        </div>
        {!b ? (
          <p className="text-sm text-[#0A0A0A]/45 p-10 text-center">Chargement…</p>
        ) : (
          <div className="p-6 md:p-8 space-y-4">
            {/* Header — offer + code */}
            <div className="pb-4 border-b border-[#0A0A0A]/10">
              <h2 className="font-display-serif text-2xl text-[#0A0A0A]">{b.offer_name || b.offer_type}</h2>
              <div className="text-sm text-[#0A0A0A]/55 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>Réf. #{b.id.slice(0, 8).toUpperCase()}</span>
                {b.reservation_code && (
                  <>
                    <span className="text-[#0A0A0A]/25">·</span>
                    <span data-testid="drawer-reservation-code">
                      Code : <span className="text-[#B8922A] font-mono font-medium">{b.reservation_code}</span>
                    </span>
                  </>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <PaymentBadge booking={b} />
                {b.status && (
                  <span className="text-[0.6rem] uppercase tracking-[0.18em] px-2 py-0.5 border border-[#0A0A0A]/15 text-[#0A0A0A]/70">
                    {b.status}
                  </span>
                )}
              </div>
            </div>

            {/* RÉSERVATION */}
            <DrawerSection label="Réservation" testid="section-reservation">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                <DrawerRow label="Offre" value={b.offer_name || b.offer_type} testid="row-offer" />
                <DrawerRow label="Univers" value={universeFR} />
                <DrawerRow label="Code réservation" value={b.reservation_code || "Non renseigné"} />
                <DrawerRow label="Source" value={sourceLabel} />
                <DrawerRow
                  label="Créée le"
                  value={fmtFrDateTime(b.created_at)}
                  testid="row-created-at"
                />
                <DrawerRow label="Date de l'expérience" value={fmtFrDate(b.date)} testid="row-experience-date" />
                {(b.boat_time || b.return_boat_time) && (
                  <>
                    <DrawerRow label="Heure de venue" value={b.boat_time || "Non renseigné"} testid="row-boat-time" />
                    {b.return_boat_time && <DrawerRow label="Retour" value={b.return_boat_time} />}
                  </>
                )}
                {b.checkout_date && (
                  <>
                    <DrawerRow label="Date d'arrivée" value={fmtFrDate(b.date)} />
                    <DrawerRow label="Date de départ" value={fmtFrDate(b.checkout_date)} />
                    <DrawerRow label="Nuits" value={`${b.nights || 0} nuit${(b.nights || 0) > 1 ? "s" : ""}`} />
                  </>
                )}
                {b.room_tier_name && (
                  <DrawerRow
                    label="Type de chambre"
                    value={`${b.rooms || 1} × ${b.room_tier_name}`}
                    testid="row-room-tier"
                  />
                )}
                {b.special_event_id && (
                  <DrawerRow label="Événement" value={b.event_title || b.special_event_id.slice(0, 8)} />
                )}
              </div>
            </DrawerSection>

            {/* CLIENT */}
            <DrawerSection label="Client" testid="section-client">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                <DrawerRow label="Nom complet" value={clientName} testid="row-client-name" />
                <DrawerRow label="Téléphone" value={primary.phone || b.phone || "Non renseigné"} testid="row-client-phone" />
                <DrawerRow label="E-mail" value={primary.email || b.email || "Non renseigné"} testid="row-client-email" />
                {primary.nationality && <DrawerRow label="Nationalité" value={primary.nationality} />}
              </div>
            </DrawerSection>

            {/* PARTICIPANTS */}
            <DrawerSection label="Participants" testid="section-participants">
              <ParticipantsBreakdown b={b} />
              {(b.participants || []).length > 0 && (
                <ul className="mt-3 space-y-2">
                  {(b.participants || []).map((p, i) => (
                    <li key={i} className="text-[0.82rem] text-[#0A0A0A]/75 border-l-2 border-[#B8922A]/25 pl-3">
                      <div className="flex flex-wrap items-center gap-x-2">
                        <strong className="text-[#0A0A0A]">{p.surname} {p.name}</strong>
                        <span className="text-[0.6rem] uppercase tracking-[0.18em] text-[#0A0A0A]/45">
                          · {p.kind || "adult"}
                        </span>
                        {p.gender && (
                          <span className="text-[0.6rem] uppercase tracking-[0.18em] text-[#0A0A0A]/55">
                            · {p.gender === "male" ? "Homme" : "Femme"}
                          </span>
                        )}
                        {p.age_bracket && (
                          <span className="text-[0.6rem] uppercase tracking-[0.18em] text-[#B8922A]">
                            · {p.age_bracket} ans
                          </span>
                        )}
                        {p.nationality && <span className="text-[#0A0A0A]/55">· {p.nationality}</span>}
                      </div>
                      {/* iter-113 · Sunset Club CRM fields */}
                      {(p.referred_by || p.snap_handle) && (
                        <div className="text-[0.7rem] text-[#0A0A0A]/60 mt-0.5 flex flex-wrap gap-x-3">
                          {p.referred_by && <span>Recommandé par&nbsp;: <strong className="text-[#0A0A0A]/80">{p.referred_by}</strong></span>}
                          {p.snap_handle && <span>Snap&nbsp;: <strong className="text-[#0A0A0A]/80">{p.snap_handle}</strong></span>}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </DrawerSection>

            {/* DÉTAIL FINANCIER */}
            <DrawerSection label="Détail financier" testid="section-financial">
              <FinancialBreakdown b={b} />
              {b.payment_method && (
                <div className="text-[0.78rem] text-[#0A0A0A]/60 mt-2">
                  Mode de paiement : <span className="text-[#0A0A0A] font-medium">{PAYMENT_METHOD_FR[b.payment_method] || b.payment_method}</span>
                  {b.payment_method === "deposit" && b.deposit_pct && ` (${b.deposit_pct}%)`}
                </div>
              )}
            </DrawerSection>

            {/* HISTORIQUE */}
            <DrawerSection label="Historique" testid="section-history">
              <ul className="text-[0.85rem] text-[#0A0A0A]/80 space-y-1.5">
                <li className="flex justify-between">
                  <span>Réservation créée</span>
                  <strong className="tabular-nums">{fmtFrDateTime(b.created_at)}</strong>
                </li>
                {b.paid_at && (
                  <li className="flex justify-between">
                    <span>Paiement encaissé</span>
                    <strong className="tabular-nums">{fmtFrDateTime(b.paid_at)}</strong>
                  </li>
                )}
                {b.cash_pending_since && !b.paid_at && (
                  <li className="flex justify-between">
                    <span>Cash en attente depuis</span>
                    <strong className="tabular-nums">{fmtFrDateTime(b.cash_pending_since)}</strong>
                  </li>
                )}
                {b.checked_in_at && (
                  <li className="flex justify-between">
                    <span>Enregistrement au quai</span>
                    <strong className="tabular-nums">{fmtFrDateTime(b.checked_in_at)}</strong>
                  </li>
                )}
                {b.embarked_at && (
                  <li className="flex justify-between">
                    <span>Embarquement</span>
                    <strong className="tabular-nums">{fmtFrDateTime(b.embarked_at)}</strong>
                  </li>
                )}
                {Array.isArray(b.qr_codes) && b.qr_codes.length > 0 && (
                  <li className="flex justify-between">
                    <span>Pass émis</span>
                    <strong className="tabular-nums">{b.qr_codes.length}</strong>
                  </li>
                )}
              </ul>
            </DrawerSection>

            {b.special_requests && (
              <DrawerSection label="Demandes spéciales" testid="section-special">
                <p className="text-sm text-[#0A0A0A]/80 bg-[#FAFAF7] p-3">{b.special_requests}</p>
              </DrawerSection>
            )}

            <DrawerSection label="Actions" testid="section-actions">
              <div className="space-y-3">
                <CheckinAndIssuePassButton booking={b} onDone={(next) => setB(next)} />
                <ResendTicketEmailButton bookingId={id} email={b.email} status={b.status} />
              </div>
            </DrawerSection>
          </div>
        )}
      </aside>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════
const UNIVERSE_TABS = [
  { id: "all", label: "Toutes" },
  { id: "hotel", label: "Hôtel" },
  { id: "beach_life", label: "Beach Life" },
];

export default function StaffReservations() {
  const [searchParams] = useSearchParams();
  const initialUniverse = searchParams.get("universe");
  const [view, setView] = useState("list");
  const [tab, setTab] = useState(
    initialUniverse && UNIVERSES[initialUniverse] ? initialUniverse : "all"
  );
  const [paymentStatus, setPaymentStatus] = useState("");
  const [search, setSearch] = useState("");
  const [bookings, setBookings] = useState([]);
  const [calendarData, setCalendarData] = useState({ by_date: {} });
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pendingCount, setPendingCount] = useState(null);
  const [exporting, setExporting] = useState(null);
  // iter-108 · Dynamic offer catalog + granular filters (offer, event, tier, dates)
  const [catalog, setCatalog] = useState({ offers: [], universes: {} });
  // Selected filter value: format is either `offer:<offer_type>` /
  // `offer:hebergement:<room_tier>` / `event:<special_event_id>` / "" for all.
  const [offerFilter, setOfferFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Fetch the dynamic offer catalog once — auto-refreshes when a new offer or
  // special event is created in the CMS (staff will just re-open the page).
  useEffect(() => {
    api.get("/staff/bookings/offer-catalog")
      .then((r) => setCatalog(r.data || { offers: [], universes: {} }))
      .catch(() => setCatalog({ offers: [], universes: {} }));
  }, []);

  const offerTypesParam = useMemo(() => {
    const cfg = UNIVERSES[tab];
    return cfg ? cfg.offer_types.join(",") : null;
  }, [tab]);

  // iter-108 · Parse the offerFilter string into concrete API params. The
  // format encodes both offer_type / special_event_id / room_tier so a single
  // dropdown drives the whole filtering surface.
  const offerFilterParams = useMemo(() => {
    if (!offerFilter) return null;
    const [kind, ...rest] = offerFilter.split(":");
    if (kind === "offer") {
      const [offerType, roomTier] = rest;
      const out = { offer_type: offerType };
      if (roomTier) out.room_tier = roomTier;
      return out;
    }
    if (kind === "event") {
      return { offer_type: "special_event", special_event_id: rest.join(":") };
    }
    return null;
  }, [offerFilter]);

  // Human-readable label for the current offer selection — used in the
  // "Day Pass — 42 réservations" summary strip.
  const offerFilterLabel = useMemo(() => {
    if (!offerFilter) return "";
    if (offerFilter.startsWith("offer:")) {
      const [, offerType, roomTier] = offerFilter.split(":");
      const entry = catalog.offers.find((o) => o.kind === "offer" && o.offer_type === offerType);
      if (!entry) return offerType;
      if (roomTier && entry.room_tiers) {
        const tier = entry.room_tiers.find((t) => t.id === roomTier);
        return tier ? `${entry.name_fr} — ${tier.name_fr}` : entry.name_fr;
      }
      return entry.name_fr;
    }
    if (offerFilter.startsWith("event:")) {
      const id = offerFilter.slice(6);
      const entry = catalog.offers.find((o) => o.kind === "special_event" && o.special_event_id === id);
      return entry ? entry.name_fr : "Événement spécial";
    }
    return "";
  }, [offerFilter, catalog.offers]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    // iter-108 · A specific offer / event selection overrides the universe tab.
    // If nothing is picked, fall back to the tab-driven offer_types filter.
    if (offerFilterParams) {
      Object.assign(params, offerFilterParams);
    } else if (offerTypesParam) {
      params.offer_types = offerTypesParam;
    }
    if (paymentStatus) params.payment_status = paymentStatus;
    if (search) params.search = search;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const url = view === "list" ? "/staff/bookings" : `/staff/bookings/calendar?month=${month}`;
    const config = view === "list" ? { params } : {};
    api.get(url, config)
      .then((r) => {
        if (view === "list") setBookings(r.data);
        else setCalendarData(r.data);
      })
      .catch((e) => {
        const code = e.response?.status;
        if (code === 403) toast.error("Accès refusé — droits insuffisants");
        else toast.error("Erreur de chargement des réservations");
      })
      .finally(() => setLoading(false));
  }, [view, offerTypesParam, offerFilterParams, paymentStatus, search, month, dateFrom, dateTo, refreshKey]);

  // Pending count (independent of filters) — shown as a small alert banner
  useEffect(() => {
    api.get("/staff/bookings/pending", { params: { days: 90 } })
      .then((r) => setPendingCount(r.data.total || 0))
      .catch(() => setPendingCount(null));
  }, [refreshKey]);

  const refresh = () => setRefreshKey((x) => x + 1);

  const doExport = async (fmt) => {
    setExporting(fmt);
    try {
      const params = new URLSearchParams();
      params.set("fmt", fmt);
      if (offerFilterParams) {
        Object.entries(offerFilterParams).forEach(([k, v]) => params.set(k, v));
      } else if (offerTypesParam) {
        params.set("offer_types", offerTypesParam);
      }
      if (paymentStatus) params.set("payment_status", paymentStatus);
      if (search) params.set("search", search);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const res = await api.get(`/staff/bookings/export?${params.toString()}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bbr_reservations_${new Date().toISOString().slice(0, 10)}.${fmt === "xlsx" ? "xlsx" : "csv"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Export ${fmt.toUpperCase()} téléchargé`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'export");
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="p-4 md:p-8 lg:p-10 max-w-[1500px] mx-auto" data-testid="staff-reservations">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display-serif text-3xl md:text-4xl text-[#0A0A0A]">Toutes les réservations</h1>
          <p className="text-sm text-[#0A0A0A]/55 mt-1">
            Réservations confirmées (paniers abandonnés exclus)
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
          <button
            onClick={() => doExport("csv")}
            disabled={!!exporting || loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[0.7rem] uppercase tracking-[0.22em] border border-[#0A0A0A]/15 hover:border-[#B8922A] hover:text-[#B8922A] disabled:opacity-50"
            data-testid="export-csv-btn"
          >
            <FileTextIcon size={13} /> {exporting === "csv" ? "Export…" : "Export CSV"}
          </button>
          <button
            onClick={() => doExport("xlsx")}
            disabled={!!exporting || loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[0.7rem] uppercase tracking-[0.22em] border border-[#0A0A0A]/15 hover:border-[#B8922A] hover:text-[#B8922A] disabled:opacity-50"
            data-testid="export-xlsx-btn"
          >
            <FileSpreadsheet size={13} /> {exporting === "xlsx" ? "Export…" : "Export Excel"}
          </button>
          <button
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[0.7rem] uppercase tracking-[0.22em] border border-[#0A0A0A]/15 hover:border-[#B8922A] hover:text-[#B8922A] disabled:opacity-50"
            data-testid="reservations-refresh"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Rafraîchir
          </button>
        </div>
      </div>

      {/* Pending-cart alert banner */}
      {pendingCount !== null && pendingCount > 0 && (
        <Link
          to="/staff/reservations/en-attente"
          className="mb-5 flex items-center justify-between gap-3 border border-[#B8922A]/40 bg-[#FFF7DA]/60 hover:bg-[#FFF3C4] px-4 py-3 transition-colors group"
          data-testid="pending-banner"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle size={16} className="text-[#B8922A]" />
            <div>
              <div className="text-[0.7rem] uppercase tracking-[0.22em] text-[#B8922A] font-medium">
                {pendingCount} réservation{pendingCount > 1 ? "s" : ""} en attente de paiement
              </div>
              <div className="text-[0.72rem] text-[#0A0A0A]/60">
                Relance automatique programmée (5h → email · 24h → email + WhatsApp)
              </div>
            </div>
          </div>
          <span className="text-[0.7rem] uppercase tracking-[0.22em] text-[#B8922A] group-hover:underline">
            Voir la liste →
          </span>
        </Link>
      )}

      {/* Universe tabs */}
      <div className="flex flex-wrap gap-1 border-b border-[#0A0A0A]/10 mb-6" data-testid="universe-tabs">
        {UNIVERSE_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-[0.7rem] uppercase tracking-[0.22em] border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? "border-[#B8922A] text-[#B8922A] font-medium"
                : "border-transparent text-[#0A0A0A]/55 hover:text-[#0A0A0A]"
            }`}
            data-testid={`universe-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters + view toggle */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0A0A0A]/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Code réservation, nom, téléphone, email, réf…"
            className="w-full border border-[#0A0A0A]/15 pl-9 pr-3 py-2 text-sm focus:border-[#B8922A] outline-none"
            data-testid="reservations-search"
          />
        </div>
        <select
          value={offerFilter}
          onChange={(e) => setOfferFilter(e.target.value)}
          className="border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none min-w-[220px]"
          data-testid="filter-offer"
        >
          <option value="">Toutes les offres</option>
          {Object.entries(catalog.universes || {}).map(([uid, ulabel]) => {
            const inUniverse = catalog.offers.filter((o) => o.universe === uid);
            if (inUniverse.length === 0) return null;
            return (
              <optgroup key={uid} label={ulabel}>
                {inUniverse.map((o) => (
                  <FilterOfferOptions key={o.id || o.offer_type} o={o} />
                ))}
              </optgroup>
            );
          })}
        </select>
        <select
          value={paymentStatus}
          onChange={(e) => setPaymentStatus(e.target.value)}
          className="border border-[#0A0A0A]/15 px-3 py-2 text-sm focus:border-[#B8922A] outline-none"
          data-testid="filter-payment"
        >
          <option value="">Tous paiements</option>
          <option value="paid">Payé</option>
          <option value="unpaid">Solde à percevoir</option>
        </select>
        <div className="flex border border-[#0A0A0A]/15 ml-auto">
          <button
            onClick={() => setView("list")}
            className={`px-3 py-2 inline-flex items-center gap-1.5 text-[0.7rem] uppercase tracking-[0.18em] ${
              view === "list" ? "bg-[#B8922A] text-white" : "text-[#0A0A0A]/55 hover:text-[#0A0A0A]"
            }`}
            data-testid="view-list"
          >
            <ListIcon size={12} /> Liste
          </button>
          <button
            onClick={() => setView("calendar")}
            className={`px-3 py-2 inline-flex items-center gap-1.5 text-[0.7rem] uppercase tracking-[0.18em] ${
              view === "calendar" ? "bg-[#B8922A] text-white" : "text-[#0A0A0A]/55 hover:text-[#0A0A0A]"
            }`}
            data-testid="view-calendar"
          >
            <CalendarDays size={12} /> Calendrier
          </button>
        </div>
      </div>

      {/* Date range + reset filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <label className="text-[0.68rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">
          Date exp. du
        </label>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="border border-[#0A0A0A]/15 px-2 py-1.5 text-sm focus:border-[#B8922A] outline-none"
          data-testid="filter-date-from"
        />
        <label className="text-[0.68rem] uppercase tracking-[0.22em] text-[#0A0A0A]/55">au</label>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="border border-[#0A0A0A]/15 px-2 py-1.5 text-sm focus:border-[#B8922A] outline-none"
          data-testid="filter-date-to"
        />
        {(offerFilter || dateFrom || dateTo || paymentStatus || search) && (
          <button
            onClick={() => {
              setOfferFilter("");
              setDateFrom("");
              setDateTo("");
              setPaymentStatus("");
              setSearch("");
            }}
            className="text-[0.68rem] uppercase tracking-[0.22em] text-[#B8922A] hover:underline"
            data-testid="filter-reset"
          >
            Réinitialiser les filtres
          </button>
        )}
      </div>

      {/* Summary line — visible feedback of the current filter context */}
      {view === "list" && !loading && (
        <div
          className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm"
          data-testid="filter-summary"
        >
          <span className="font-medium text-[#0A0A0A]">
            {offerFilterLabel || (tab !== "all" ? UNIVERSES[tab]?.label : "Toutes les offres")}
          </span>
          <span className="text-[#0A0A0A]/45">·</span>
          <span className="text-[#B8922A]">
            {bookings.length} réservation{bookings.length > 1 ? "s" : ""}
          </span>
          {(dateFrom || dateTo) && (
            <>
              <span className="text-[#0A0A0A]/45">·</span>
              <span className="text-[#0A0A0A]/70">
                {dateFrom || "…"} → {dateTo || "…"}
              </span>
            </>
          )}
        </div>
      )}

      {/* Content */}
      <div className="bg-white border border-[#0A0A0A]/8 p-5 md:p-6">
        {loading ? (
          <p className="text-sm text-[#0A0A0A]/45 py-12 text-center">Chargement…</p>
        ) : view === "list" ? (
          <BookingsList bookings={bookings} onOpen={setOpenId} />
        ) : (
          <BookingsCalendar
            month={month}
            onChangeMonth={setMonth}
            byDate={calendarData.by_date}
            onOpen={setOpenId}
          />
        )}
      </div>

      <BookingDrawer id={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
function ResendTicketEmailButton({ bookingId, email, status }) {
  const [busy, setBusy] = useState(false);
  const canSend = !!email && ["paid", "completed", "confirmed"].includes(status);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/staff/bookings/${bookingId}/resend-ticket-email`);
      if (data?.ok) {
        toast.success(`Email envoyé à ${email}`);
      } else {
        toast.error(data?.message || data?.error || "Échec de l'envoi");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    } finally {
      setBusy(false);
    }
  };
  if (!canSend) {
    return (
      <div className="text-[0.7rem] text-[#0A0A0A]/45 italic">
        {!email
          ? "Aucune adresse email — envoi impossible."
          : "Le billet ne peut être renvoyé que pour les réservations payées."}
      </div>
    );
  }
  return (
    <button
      onClick={send}
      disabled={busy}
      className="w-full px-4 py-2.5 bg-[#B8922A] hover:bg-[#9c7c1f] text-white text-xs uppercase tracking-[0.18em] inline-flex items-center justify-center gap-2 disabled:opacity-50"
      data-testid="resend-ticket-email-btn"
    >
      <Mail size={13} /> {busy ? "Envoi…" : `Renvoyer la confirmation à ${email}`}
    </button>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// iter-102 · One-click dock check-in from the Réservations globales drawer.
// Uses the universal /staff/agent-billed/checkin/{id} endpoint (iter-101).
// A single click:
//   1. Generates the boarding pass(es) with QR code(s)
//   2. Marks the booking as `checked_in_at = now`
//   3. Creates a client-kind registration row so the person shows in
//      « Enregistrement simple » alongside their offer / date / etc.
// The button is idempotent — if the pass already exists it shows a green
// "Enregistré" chip and the drawer's other actions remain available.
// ═══════════════════════════════════════════════════════════════════════
function CheckinAndIssuePassButton({ booking, onDone }) {
  const [busy, setBusy] = useState(false);
  const alreadyCheckedIn = Array.isArray(booking?.qr_codes) && booking.qr_codes.length > 0;
  const eligibleStatus = ["confirmed", "arrived", "completed", "pending_cash_payment"].includes(booking?.status);
  const canDo = eligibleStatus && !alreadyCheckedIn;

  const doCheckin = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/staff/agent-billed/checkin/${booking.id}`);
      const nextBooking = data?.booking || booking;
      // Best-effort mirror into Enregistrement simple as a Client registration.
      try {
        const primary = (booking.participants || []).find((p) => p.kind === "adult") || {};
        await api.post("/staff/registrations/from-booking", {
          booking_id: booking.id,
          first_name: primary.name || "",
          last_name: primary.surname || "",
          email: primary.email || booking.email || "",
          phone: primary.phone || booking.phone || "",
          nationality: primary.nationality || "",
        });
      } catch (_) {
        /* silent — the pass generation is the primary source of truth. */
      }
      toast.success(
        data?.already_checked_in
          ? "Passager déjà enregistré — pass déjà émis."
          : `Enregistrement OK — ${data?.issued || nextBooking.qr_codes?.length || 0} pass émis.`,
      );
      onDone?.(nextBooking);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  if (alreadyCheckedIn) {
    return (
      <div
        className="w-full px-4 py-2.5 bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs uppercase tracking-[0.18em] inline-flex items-center justify-center gap-2"
        data-testid="checkin-done-chip"
      >
        <CheckCircle2 size={14} /> Pass déjà émis ({booking.qr_codes.length})
      </div>
    );
  }
  if (!canDo) {
    return (
      <div className="text-[0.7rem] text-[#0A0A0A]/45 italic">
        Réservation non confirmée — enregistrement impossible.
      </div>
    );
  }
  return (
    <button
      onClick={doCheckin}
      disabled={busy}
      className="w-full px-4 py-2.5 bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.18em] inline-flex items-center justify-center gap-2 disabled:opacity-50"
      data-testid="checkin-issue-pass-btn"
    >
      <TicketCheck size={13} /> {busy ? "Enregistrement…" : "Enregistrer + générer le pass"}
    </button>
  );
}
